// RefundModal.tsx
import React, { useState, useEffect } from "react";
import TypedModal from "@/components/TypedModal/TypedModal";
import {
  X,
  DollarSign,
  AlertCircle,
  Loader2,
  CreditCard,
  Info,
  Check,
  Users,
  Receipt,
  ChevronDown,
  Plus,
  Minus,
  ArrowLeft,
  Clock,
  AlertTriangle,
} from "lucide-react";
import CustomCleaveInput from "@/components/CustomCleaveInput/CustomCleaveInput";
import { RefundModalProps } from "@/modules/eventRegistration/features/EventWaitlist/components/RefundModal/types";
import styles from "./RefundModal.module.css";

export default function RefundModal({
  isOpen,
  onClose,
  selectedRefund,
  refundReason,
  onReasonChange,
  onProcessRefund,
  isGroupRefund = false,
  isProcessing = false,
}: RefundModalProps) {
  const [refundType, setRefundType] = useState<string>("full");
  const [partialAmount, setPartialAmount] = useState<number>(0);
  const [notifyAttendee, setNotifyAttendee] = useState<boolean>(true);
  const [seatsToRefund, setSeatsToRefund] = useState<number>(1);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen && selectedRefund) {
      setRefundType("full");
      setPartialAmount(0);
      setNotifyAttendee(true);
      // Set seats to refund to 1 or the max available, whichever is smaller
      const maxSeats = selectedRefund.remainingRefundableSeats || 1;
      setSeatsToRefund(Math.min(1, maxSeats));
      setShowConfirmation(false);
    }
  }, [isOpen, selectedRefund]);

  if (!selectedRefund) return null;

  const pricePerSeat = selectedRefund.amount;
  const maxRefundableSeats = selectedRefund.remainingRefundableSeats || 0;
  const maxRefundableAmount = selectedRefund.remainingRefundableAmount || 0;
  const fullAmount = pricePerSeat * seatsToRefund;
  const amountToRefund = refundType === "full" ? fullAmount : partialAmount;

  // Count pending refunds
  const pendingRefunds =
    selectedRefund.refundHistory?.filter(
      (r) => r.status === "pending" || r.status === "processing",
    ) || [];

  const handleClose = () => {
    if (!isProcessing) {
      onClose();
    }
  };

  const handleProcessRefund = () => {
    if (!refundReason.trim()) return;
    if (
      refundType === "partial" &&
      (partialAmount <= 0 || partialAmount > maxRefundableAmount)
    )
      return;
    if (refundType === "full" && fullAmount > maxRefundableAmount) return;
    setShowConfirmation(true);
  };

  const confirmRefund = () => {
    onProcessRefund(refundType, partialAmount, notifyAttendee, seatsToRefund);
  };

  const handleIncrementSeats = () => {
    if (seatsToRefund < maxRefundableSeats) {
      const newSeats = seatsToRefund + 1;
      // Check if this would exceed the max refundable amount
      if (pricePerSeat * newSeats <= maxRefundableAmount) {
        setSeatsToRefund(newSeats);
      }
    }
  };

  const handleDecrementSeats = () => {
    if (seatsToRefund > 1) {
      setSeatsToRefund(seatsToRefund - 1);
    }
  };

  const modalStyles = {
    content: {
      position: "fixed" as const,
      top: "0",
      left: "0",
      right: "0",
      bottom: "0",
      padding: "0",
      background: "white",
      overflow: "hidden",
      border: "none",
      borderRadius: "0",
    },
    overlay: {
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      zIndex: 9999,
    },
  };

  // Handle case where there are no funds left to refund
  if (maxRefundableAmount <= 0 || maxRefundableSeats <= 0) {
    return (
      <TypedModal
        contentLabel="Process Refund"
        isOpen={isOpen}
        onRequestClose={handleClose}
        style={modalStyles}
      >
        <div className={styles.modalContainer}>
          <div className={styles.modalHeader}>
            <div className={styles.headerContent}>
              <div className={styles.headerLeft}>
                <div
                  className={`${styles.headerIcon} ${styles.headerIconError}`}
                >
                  <AlertCircle className={styles.headerIconSvg} />
                </div>
                <div>
                  <h2 className={styles.headerTitle}>Cannot Process Refund</h2>
                  <p className={styles.headerSubtitle}>
                    No refundable amount remaining
                  </p>
                </div>
              </div>
              <button
                aria-label="Close modal"
                className={styles.closeButton}
                onClick={handleClose}
                type="button"
              >
                <X className={styles.closeIcon} />
              </button>
            </div>
          </div>
          <div className={styles.modalBody}>
            <div className={styles.modalContent}>
              <div className={styles.errorCard}>
                <p className={styles.errorText}>
                  {selectedRefund.hasPendingRefunds
                    ? "All funds have been refunded or are pending refund for this group."
                    : "All funds have been refunded for this group."}
                </p>
                {pendingRefunds.length > 0 && (
                  <div className={styles.pendingRefundsInfo}>
                    <h4 className={styles.pendingRefundsTitle}>
                      <Clock className={styles.pendingRefundsIcon} />
                      Pending Refunds
                    </h4>
                    {pendingRefunds.map((refund) => (
                      <div className={styles.pendingRefundItem} key={refund.id}>
                        <span>${refund.requestedAmount.toFixed(2)}</span>
                        <span className={styles.pendingRefundDate}>
                          {new Date(refund.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className={styles.modalFooter}>
            <div className={styles.footerContent}>
              <div className={styles.footerLeft}></div>
              <div className={styles.footerActions}>
                <button
                  className={styles.primaryButton}
                  onClick={handleClose}
                  type="button"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </TypedModal>
    );
  }

  return (
    <TypedModal
      contentLabel="Process Refund"
      isOpen={isOpen}
      onRequestClose={handleClose}
      style={modalStyles}
    >
      <div className={styles.modalContainer}>
        {/* Fixed Header */}
        <div className={styles.modalHeader}>
          <div className={styles.headerContent}>
            <div className={styles.headerLeft}>
              <div className={styles.headerIcon}>
                <DollarSign className={styles.headerIconSvg} />
              </div>
              <div>
                <h2 className={styles.headerTitle}>Process Refund</h2>
                <p className={styles.headerSubtitle}>
                  {isGroupRefund ? (
                    <>
                      Refund for{" "}
                      <span className={styles.emphasis}>
                        {selectedRefund.attendeeName}
                      </span>
                      {maxRefundableSeats > 0 && (
                        <span className={styles.subtitleDetail}>
                          • {maxRefundableSeats} refundable seat
                          {maxRefundableSeats !== 1 ? "s" : ""}
                        </span>
                      )}
                    </>
                  ) : (
                    <>
                      Refund for{" "}
                      <span className={styles.emphasis}>
                        {selectedRefund.attendeeName}
                      </span>
                    </>
                  )}
                </p>
              </div>
            </div>
            <button
              aria-label="Close modal"
              className={styles.closeButton}
              onClick={handleClose}
              type="button"
            >
              <X className={styles.closeIcon} />
            </button>
          </div>
        </div>

        {/* Info Banner */}
        {!showConfirmation && (
          <div className={styles.infoBanner}>
            <Info className={styles.infoBannerIcon} />
            <p className={styles.infoBannerText}>
              Processing a refund will credit the amount back to the original
              payment method. This action cannot be undone.
            </p>
          </div>
        )}

        {/* Warning Banner for Pending Refunds */}
        {!showConfirmation &&
          selectedRefund.hasPendingRefunds &&
          pendingRefunds.length > 0 && (
            <div className={styles.warningBanner}>
              <AlertTriangle className={styles.warningBannerIcon} />
              <p className={styles.warningBannerText}>
                There {pendingRefunds.length === 1 ? "is" : "are"}{" "}
                {pendingRefunds.length} pending refund
                {pendingRefunds.length === 1 ? "" : "s"} totaling $
                {pendingRefunds
                  .reduce((sum, r) => sum + r.requestedAmount, 0)
                  .toFixed(2)}
                . Additional refunds are limited to remaining amounts.
              </p>
            </div>
          )}

        {/* Scrollable Content */}
        <div className={styles.modalBody}>
          <div className={styles.modalContent}>
            {!showConfirmation ? (
              <div className={styles.formContainer}>
                {/* Refund Limits Card */}
                <div className={styles.limitsCard}>
                  <h3 className={styles.cardTitle}>
                    <Info className={styles.cardIcon} />
                    Refund Limits
                  </h3>
                  <div className={styles.limitsGrid}>
                    <div className={styles.limitItem}>
                      <span className={styles.limitLabel}>
                        Maximum Refundable Amount:
                      </span>
                      <span className={styles.limitValue}>
                        ${maxRefundableAmount.toFixed(2)}
                      </span>
                    </div>
                    {isGroupRefund && (
                      <div className={styles.limitItem}>
                        <span className={styles.limitLabel}>
                          Maximum Refundable Seats:
                        </span>
                        <span className={styles.limitValue}>
                          {maxRefundableSeats}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Refund Details Card */}
                <div className={styles.detailsCard}>
                  <h3 className={styles.cardTitle}>
                    <Receipt className={styles.cardIcon} />
                    Refund Details
                  </h3>

                  {/* Seats to Refund - Only for group refunds */}
                  {isGroupRefund && maxRefundableSeats > 0 && (
                    <div className={styles.formGroup}>
                      <label className={styles.formLabel}>
                        Number of Seats to Refund
                      </label>
                      <div className={styles.numberInputContainer}>
                        <button
                          aria-label="Decrease seat count"
                          className={styles.numberButton}
                          disabled={seatsToRefund <= 1}
                          onClick={handleDecrementSeats}
                          type="button"
                        >
                          <Minus className={styles.numberButtonIcon} />
                        </button>
                        <div className={styles.numberDisplay}>
                          <span className={styles.numberValue}>
                            {seatsToRefund}
                          </span>
                          <span className={styles.numberLabel}>
                            seat{seatsToRefund !== 1 ? "s" : ""}
                          </span>
                        </div>
                        <button
                          aria-label="Increase seat count"
                          className={styles.numberButton}
                          disabled={
                            seatsToRefund >= maxRefundableSeats ||
                            pricePerSeat * (seatsToRefund + 1) >
                              maxRefundableAmount
                          }
                          onClick={handleIncrementSeats}
                          type="button"
                        >
                          <Plus className={styles.numberButtonIcon} />
                        </button>
                      </div>
                      <p className={styles.numberHint}>
                        Maximum: {maxRefundableSeats} seats • $
                        {pricePerSeat.toFixed(2)} per seat
                      </p>
                    </div>
                  )}

                  {/* Refund Type Selection */}
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Refund Type</label>
                    <div className={styles.radioGroup}>
                      <label
                        className={`${styles.radioLabel} ${
                          refundType === "full" ? styles.radioLabelActive : ""
                        } ${fullAmount > maxRefundableAmount ? styles.radioLabelDisabled : ""}`}
                      >
                        <input
                          checked={refundType === "full"}
                          className={styles.radioInput}
                          disabled={fullAmount > maxRefundableAmount}
                          name="refundType"
                          onChange={(e) => setRefundType(e.target.value)}
                          type="radio"
                          value="full"
                        />
                        <div className={styles.radioCircle}></div>
                        <div className={styles.radioContent}>
                          <span className={styles.radioTitle}>Full Refund</span>
                          <span className={styles.radioAmount}>
                            ${fullAmount.toFixed(2)}
                          </span>
                        </div>
                      </label>
                      <label
                        className={`${styles.radioLabel} ${
                          refundType === "partial"
                            ? styles.radioLabelActive
                            : ""
                        }`}
                      >
                        <input
                          checked={refundType === "partial"}
                          className={styles.radioInput}
                          name="refundType"
                          onChange={(e) => setRefundType(e.target.value)}
                          type="radio"
                          value="partial"
                        />
                        <div className={styles.radioCircle}></div>
                        <div className={styles.radioContent}>
                          <span className={styles.radioTitle}>
                            Partial Refund
                          </span>
                        </div>
                      </label>
                    </div>
                    {refundType === "full" &&
                      fullAmount > maxRefundableAmount && (
                        <p className={styles.radioHint}>
                          <AlertCircle className={styles.radioHintIcon} />
                          Full refund amount exceeds the maximum refundable
                          amount
                        </p>
                      )}
                  </div>

                  {/* Partial Amount Input */}
                  {refundType === "partial" && (
                    <div className={styles.formGroup}>
                      <label className={styles.formLabel}>Refund Amount</label>
                      <div className={styles.cleaveInputWrapper}>
                        <CustomCleaveInput
                          className={styles.cleaveInput}
                          onChange={(rawValue) => {
                            const numValue = parseFloat(rawValue) || 0;
                            // Enforce maximum limit
                            if (numValue <= maxRefundableAmount) {
                              setPartialAmount(numValue);
                            } else {
                              setPartialAmount(maxRefundableAmount);
                            }
                          }}
                          options={{
                            numeral: true,
                            numeralDecimalScale: 2,
                            numeralThousandsGroupStyle: "thousand",
                            prefix: "$",
                            rawValueTrimPrefix: true,
                            numeralPositiveOnly: true,
                          }}
                          placeholder="0.00"
                          value={
                            partialAmount === 0 ? "" : partialAmount.toString()
                          }
                        />
                      </div>
                      {partialAmount > maxRefundableAmount && (
                        <p className={styles.errorHint}>
                          Amount cannot exceed ${maxRefundableAmount.toFixed(2)}
                        </p>
                      )}
                      <p className={styles.amountHint}>
                        Maximum refundable: ${maxRefundableAmount.toFixed(2)}
                      </p>
                    </div>
                  )}
                </div>

                {/* Refund Reason */}
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    Reason for Refund <span className={styles.required}>*</span>
                  </label>
                  <textarea
                    className={styles.textarea}
                    onChange={(e) => onReasonChange(e.target.value)}
                    placeholder="Please provide a detailed reason for the refund..."
                    rows={4}
                    value={refundReason}
                  />
                </div>

                {/* Notification Options */}
                <div className={styles.formGroup}>
                  <h4 className={styles.sectionTitle}>Notification Options</h4>
                  <div className={styles.checkboxGroup}>
                    <label className={styles.checkboxLabel}>
                      <div className={styles.checkboxWrapper}>
                        <input
                          checked={notifyAttendee}
                          className={styles.checkbox}
                          onChange={(e) => setNotifyAttendee(e.target.checked)}
                          type="checkbox"
                        />
                        <div className={styles.checkboxCustom}></div>
                      </div>
                      <div className={styles.checkboxContent}>
                        <span className={styles.checkboxTitle}>
                          Notify Attendee
                        </span>
                        <p className={styles.checkboxDescription}>
                          Send email confirmation about the refund
                        </p>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Refund Summary */}
                <div className={styles.summaryCard}>
                  <div className={styles.summaryRow}>
                    <span className={styles.summaryLabel}>
                      Total Refund Amount:
                    </span>
                    <span className={styles.summaryAmount}>
                      ${amountToRefund.toFixed(2)}
                    </span>
                  </div>
                  {isGroupRefund && seatsToRefund > 0 && (
                    <p className={styles.summaryDetail}>
                      {seatsToRefund} {seatsToRefund === 1 ? "seat" : "seats"} ×
                      ${pricePerSeat.toFixed(2)}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              /* Confirmation View */
              <div className={styles.confirmationContainer}>
                <div className={styles.successCard}>
                  <Check className={styles.successIcon} />
                  <div>
                    <p className={styles.successTitle}>
                      Ready to Process Refund
                    </p>
                    <p className={styles.successText}>
                      You are about to refund{" "}
                      <strong>${amountToRefund.toFixed(2)}</strong> to{" "}
                      {selectedRefund.attendeeName}.
                    </p>
                  </div>
                </div>

                <div className={styles.summaryCard}>
                  <h4 className={styles.summaryTitle}>Refund Summary</h4>
                  <div className={styles.summaryContent}>
                    <div className={styles.summaryItem}>
                      <span className={styles.summaryItemLabel}>
                        Refund Type:
                      </span>
                      <span className={styles.summaryItemValue}>
                        {refundType === "full"
                          ? "Full Refund"
                          : "Partial Refund"}
                      </span>
                    </div>
                    <div className={styles.summaryItem}>
                      <span className={styles.summaryItemLabel}>Amount:</span>
                      <span className={styles.summaryItemValue}>
                        ${amountToRefund.toFixed(2)}
                      </span>
                    </div>
                    {isGroupRefund && (
                      <div className={styles.summaryItem}>
                        <span className={styles.summaryItemLabel}>Seats:</span>
                        <span className={styles.summaryItemValue}>
                          {seatsToRefund} of {maxRefundableSeats} remaining
                        </span>
                      </div>
                    )}
                  </div>
                  {refundReason && (
                    <div className={styles.summaryNotes}>
                      <p className={styles.summaryNotesLabel}>Refund Reason:</p>
                      <p className={styles.summaryNotesText}>{refundReason}</p>
                    </div>
                  )}
                </div>

                <div className={styles.notificationCard}>
                  <Info className={styles.notificationIcon} />
                  <div>
                    <strong className={styles.notificationTitle}>
                      Processing Information:
                    </strong>
                    <ul className={styles.notificationList}>
                      <li>
                        Refund will be credited to the original payment method
                      </li>
                      <li>Processing may take 5-10 business days</li>
                      {notifyAttendee && (
                        <li>Email confirmation will be sent to attendee</li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Fixed Footer */}
        <div className={styles.modalFooter}>
          <div className={styles.footerContent}>
            <div className={styles.footerLeft}>
              {!showConfirmation && !refundReason.trim() && (
                <p className={styles.warningMessage}>
                  <AlertCircle className={styles.warningIcon} />
                  Please provide a refund reason
                </p>
              )}
              {!showConfirmation &&
                refundType === "partial" &&
                partialAmount <= 0 && (
                  <p className={styles.warningMessage}>
                    <AlertCircle className={styles.warningIcon} />
                    Please enter a valid refund amount
                  </p>
                )}
              {!showConfirmation &&
                refundType === "partial" &&
                partialAmount > maxRefundableAmount && (
                  <p className={styles.warningMessage}>
                    <AlertCircle className={styles.warningIcon} />
                    Amount exceeds maximum refundable amount
                  </p>
                )}
            </div>
            <div className={styles.footerActions}>
              {!showConfirmation ? (
                <>
                  <button
                    className={styles.cancelButton}
                    onClick={handleClose}
                    type="button"
                  >
                    Cancel
                  </button>
                  <button
                    className={`${styles.primaryButton} ${
                      !refundReason.trim() ||
                      (refundType === "partial" &&
                        (partialAmount <= 0 ||
                          partialAmount > maxRefundableAmount)) ||
                      (refundType === "full" &&
                        fullAmount > maxRefundableAmount)
                        ? styles.buttonDisabled
                        : ""
                    }`}
                    disabled={
                      !refundReason.trim() ||
                      (refundType === "partial" &&
                        (partialAmount <= 0 ||
                          partialAmount > maxRefundableAmount)) ||
                      (refundType === "full" &&
                        fullAmount > maxRefundableAmount) ||
                      isProcessing
                    }
                    onClick={handleProcessRefund}
                    type="button"
                  >
                    <CreditCard className={styles.buttonIcon} />
                    Continue
                  </button>
                </>
              ) : (
                <>
                  <button
                    className={styles.backButton}
                    disabled={isProcessing}
                    onClick={() => setShowConfirmation(false)}
                    type="button"
                  >
                    Back
                  </button>
                  <button
                    className={`${styles.confirmButton} ${
                      isProcessing ? styles.buttonProcessing : ""
                    }`}
                    disabled={isProcessing}
                    onClick={confirmRefund}
                    type="button"
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className={styles.spinningIcon} />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Check className={styles.buttonIcon} />
                        Confirm Refund
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </TypedModal>
  );
}
